import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'components/dialog/Modals'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import { fail, success } from 'features/toast/Toast'
import {
  createUploads,
  fetchTemplateFile,
  hideUploadModal,
  removeFile,
  setTemplateType,
  uploadFiles,
} from 'features/bulk/upload/uploadModalSlice'
import SelectBox from 'components/form/SelectBox'
import { TEMPLATE_TYPE } from 'features/bulk/BulkCodes'
import Button from 'components/form/Button'
import classNames from 'classnames'
import FileInput from 'components/form/FileInput'
import { isEmptyObject, mbToByte } from 'utils'
import api from 'api/axios/foka'
import { fetchUploadList } from 'features/bulk/upload/uploadSlice'

const options = [
  TEMPLATE_TYPE.CREATE_KEYWORD,
  TEMPLATE_TYPE.CREATE_CREATIVE_LINK,
  TEMPLATE_TYPE.CREATE_CREATIVE,
  TEMPLATE_TYPE.CREATE_ADGROUP,
  TEMPLATE_TYPE.CREATE_CAMPAIGN,
  TEMPLATE_TYPE.UPDATE_KEYWORD,
  TEMPLATE_TYPE.UPDATE_CREATIVE_LINK,
  TEMPLATE_TYPE.UPDATE_ADGROUP,
  TEMPLATE_TYPE.UPDATE_CAMPAIGN,
  TEMPLATE_TYPE.DELETE,
]

export const FILE_COUNT_LIMIT = 10
const FILE_SIZE_LIMIT = mbToByte(10)

const UploadModal = () => {
  const dispatch = useDispatch()
  const {
    isShow,
    isLoading,
    uploading,
    disabled,
    templateType,
    templateFileUrl,
    templateLoading,
    files,
  } = useSelector(state => state.bulk.uploadModal)
  const uploadDisabled = uploading || files.length >= FILE_COUNT_LIMIT
  const [zipLink, setZipLink] = useState()

  useEffect(() => {
    if (isShow) {
      api.bulk
        .getAllTemplateFile()
        .then(({ data: { downloadUrl } }) => {
          setZipLink(`${downloadUrl}?download`)
        })
        .catch(ignore => {
          fail('알 수 없는 에러가 발생했습니다')
        })
    }
  }, [isShow])

  useEffect(() => {
    if (templateType) {
      dispatch(fetchTemplateFile())
    }
  }, [dispatch, templateType])

  const handleClose = () => {
    if (uploading) {
      fail('업로드 중인 파일이 있습니다.')
    } else {
      dispatch(hideUploadModal())
    }
  }

  const handleConfirm = async () => {
    const { payload } = await dispatch(createUploads())
    if (isEmptyObject(payload)) {
      success('요청 내용이 등록되었습니다.')
    } else {
      Object.keys(payload).forEach(key => {
        const { message, fileNames } = payload[key]
        fail(`${message}\n${fileNames.join(', ')}`)
      })
    }
    dispatch(hideUploadModal())
    dispatch(fetchUploadList())
  }

  const handleFileChange = async inputFiles => {
    const fileErrors = {
      hasSizeError: false,
      hasNameError: false,
      hasCountError: false,
    }
    const maxCount = FILE_COUNT_LIMIT - files.length
    const filteredFile = inputFiles.reduce((temp, file) => {
      if (temp.length <= maxCount) {
        if (file.size > FILE_SIZE_LIMIT) {
          fileErrors.hasSizeError = true
        } else if (file.name.length > 50 + 4) {
          fileErrors.hasNameError = true
        } else {
          temp.push(file)
        }
      } else {
        fileErrors.hasCountError = true
      }
      return temp
    }, [])
    if (fileErrors.hasSizeError) {
      fail(
        '파일 요청 용량이 초과하였습니다.\n파일은 5만행 / 10MB 미만 파일로 요청 바랍니다.'
      )
    }
    if (fileErrors.hasNameError) {
      fail('파일명 50자 초과를 제외하고 등록되었습니다.')
    }
    if (fileErrors.hasCountError) {
      fail('등록 가능한 개수 초과를 제외하고 등록되었습니다.')
    }
    await dispatch(uploadFiles(filteredFile))
  }

  const handleTemplateDownload = () => {
    if (templateFileUrl) {
      window.open(templateFileUrl)
    }
  }

  return (
    <Modal className="manage_layer" on={isShow}>
      <ConfirmModal
        title="업로드 등록하기"
        handleClose={handleClose}
        onConfirm={handleConfirm}
        confirmDisabled={
          disabled || templateType === null || files.length === 0
        }>
        <div className="item_manage">
          <strong className="tit_manage">유형 선택</strong>
          <div className="bundle_inp">
            <SelectBox
              value={templateType}
              disabled={isLoading}
              placeholder="유형을 선택하세요."
              onValueChange={v => {
                dispatch(setTemplateType(v))
              }}>
              {options.map(option => (
                <SelectBox.Item value={option.name} key={option.name}>
                  [{option.methodType.text}]{option.domainType.text}
                </SelectBox.Item>
              ))}
            </SelectBox>
            <Button
              line
              onClick={handleTemplateDownload}
              disabled={
                templateType === null ||
                templateLoading ||
                templateFileUrl === null
              }>
              템플릿 다운로드
            </Button>
          </div>
          <p className="desc_info">
            프리미엄링크 캠페인 유형만 업로드 가능합니다.
            <br />
            선택한 유형의 CSV 파일을 업로드할 수 있습니다.
            <br />
            1개 파일 당 최대 5만 행 / 10mb의 용량 제한이 있습니다.
          </p>
          <a
            href={zipLink}
            className="btn_gs"
            style={{ ...(zipLink === undefined && { pointerEvents: 'none' }) }}>
            <span className="inner_g">전체 템플릿 다운로드</span>
          </a>
        </div>
        <div className="item_manage">
          <strong className="tit_manage">
            파일 첨부 <em className="fc_emph">{files.length}</em>/10
          </strong>
          <div className="itemtbl_wrap">
            <div className="item_head">
              <div
                className={classNames('btn_gm gm_bl', {
                  in_active: uploadDisabled,
                })}>
                <label htmlFor="inpFile" className="lab_file">
                  <span className="inner_g">파일업로드</span>
                </label>
                <FileInput
                  id="inpFile"
                  className="inp_file"
                  hidden={false}
                  clearFiles
                  multiple
                  accept=".csv"
                  onFileChange={handleFileChange}
                  disabled={uploadDisabled}
                />
              </div>
            </div>
            <div className="item_body">
              {files.length === 0 ? (
                <div className="nodata_info">
                  <label
                    className="link_txt"
                    htmlFor="inpFile"
                    style={{ cursor: 'pointer' }}>
                    파일 업로드
                  </label>
                  를 눌러 파일을 등록해주세요.
                </div>
              ) : (
                <ul className="list_file">
                  {files.map(({ filePath, name }) => (
                    <li key={filePath}>
                      {name}
                      <button
                        type="button"
                        className="btn_del"
                        onClick={() => {
                          dispatch(removeFile(filePath))
                        }}>
                        <span className="ico_comm">삭제</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </ConfirmModal>
    </Modal>
  )
}
export default UploadModal
